import React, { useEffect, useState } from "react";
import BASE_URL from "../../../../../assets/global/url";
import axios from "axios";
import { formatToTwoDecimal } from "../../../../../utils/numberFormatter";
import { InputGroup, FormControl, Button } from "react-bootstrap";

const PRODUCT_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "item_code", label: "Item Code" },
  { value: "product_name", label: "Product Name" },
];

const ProductTab = ({ productCategory, thisFromdate, thisTodate }) => {
  const [inventoryReport, setInventoryReport] = useState({
    inventory: [],
    total: {},
  });

  const [productSearchText, setProductSearchText] = useState("");
  const [productFilterColumn, setProductFilterColumn] = useState("all");

  const getInventoryReport = async () => {
    try {
      const baseParams = {
        startDate: thisFromdate,
        endDate: thisTodate,
      };

      const inventoryReportEndpoint = `${BASE_URL}/inventoryReport`;

      const res = await axios.get(
        `${inventoryReportEndpoint}/inventory/summary`,
        {
          params: {
            ...baseParams,
            productCategory,
          },
        },
      );

      setInventoryReport({
        inventory: res.data.inventory,
        total: res.data.total,
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getInventoryReport();
  }, [thisFromdate, productCategory]);

  const filteredInventory = inventoryReport?.inventory?.filter((item) => {
    const searchLower = productSearchText.toLowerCase();
    if (!searchLower) return true;

    const itemCode = (item.productCode || "").toLowerCase();
    const productName = (item.productName || "").toLowerCase();

    if (productFilterColumn === "item_code")
      return itemCode.includes(searchLower);
    if (productFilterColumn === "product_name")
      return productName.includes(searchLower);

    return itemCode.includes(searchLower) || productName.includes(searchLower);
  });

  const activeFilterLabel =
    PRODUCT_FILTER_OPTIONS.find((o) => o.value === productFilterColumn)
      ?.label ?? "All";

  // UPDATED: Using outline instead of borders paints over transparent sub-pixel cracks
  // Colors come from CSS variables so dark mode can override them
  // (--report-th-bg / --report-th-border are set in style.css).
  const stickyThStyle = {
    backgroundColor: "var(--report-th-bg, #f8f9fa)",
    outline: "1px solid var(--report-th-border, #dee2e6)",
    outlineOffset: "-1px",
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-sm mb-3">
          <div className="w-100 d-flex align-items-center mt-3 mb-3">
            <h5>{productCategory} Inventory</h5>
            <hr className="flex-grow-1 mx-3" />
          </div>

          <div className="mt-3 mb-4">
            <InputGroup className="mb-3">
              <FormControl
                type="text"
                placeholder={`Search by ${activeFilterLabel}…`}
                value={productSearchText}
                onChange={(e) => setProductSearchText(e.target.value)}
              />

              <Button
                type="button"
                variant="outline-secondary"
                className="dropdown-toggle-split"
                data-bs-toggle="dropdown"
                aria-expanded="false">
                <i className="fa-solid fa-sliders" />
              </Button>

              <ul className="dropdown-menu dropdown-menu-end">
                {PRODUCT_FILTER_OPTIONS.map(({ value, label }) => (
                  <li key={value}>
                    <button
                      className={`dropdown-item ${productFilterColumn === value ? "active" : ""}`}
                      onClick={() => setProductFilterColumn(value)}>
                      {label}
                    </button>
                  </li>
                ))}
              </ul>
            </InputGroup>
          </div>

          <div
            className="table-responsive"
            style={{ maxHeight: "calc(100vh - 350px)", overflowY: "auto" }}>
            <table className="table table-bordered table-striped">
              <thead
                className="thead-light"
                style={{
                  position: "sticky",
                  top: "-1px", // UPDATED: Pushes the header up exactly 1px to close the top gap
                  zIndex: 2,
                }}>
                <tr>
                  <th
                    rowSpan="2"
                    className="align-middle"
                    style={stickyThStyle}>
                    Item Code
                  </th>
                  <th
                    rowSpan="2"
                    className="align-middle"
                    style={stickyThStyle}>
                    Product Name
                  </th>
                  <th colSpan="3" className="text-center" style={stickyThStyle}>
                    Beginning Inventory
                  </th>
                  <th colSpan="3" className="text-center" style={stickyThStyle}>
                    Product In
                  </th>
                  <th colSpan="3" className="text-center" style={stickyThStyle}>
                    Product Out
                  </th>
                  <th colSpan="3" className="text-center" style={stickyThStyle}>
                    Final Inventory
                  </th>
                </tr>
                <tr>
                  <th style={stickyThStyle}>Quantity</th>
                  <th style={stickyThStyle}>Avg. Price</th>
                  <th style={stickyThStyle}>Amount (₱)</th>
                  <th style={stickyThStyle}>Quantity</th>
                  <th style={stickyThStyle}>Avg. Price</th>
                  <th style={stickyThStyle}>Amount (₱)</th>
                  <th style={stickyThStyle}>Quantity</th>
                  <th style={stickyThStyle}>Avg. Price</th>
                  <th style={stickyThStyle}>Amount (₱)</th>
                  <th style={stickyThStyle}>Quantity</th>
                  <th style={stickyThStyle}>Avg. Price</th>
                  <th style={stickyThStyle}>Amount (₱)</th>
                </tr>
              </thead>
              <tbody>
                {filteredInventory?.map((item) =>
                  // prettier-ignore
                  <React.Fragment key={item.product_id}>
                    <tr>
                      <td>{item.productCode}</td>
                      <td>{item.productName}</td>
                      {/* Beginning Inventory */}
                      <td>{formatToTwoDecimal(item.beginningInventoryQuantity)}</td>
                      <td>{formatToTwoDecimal(item.beginningInventoryAveragePrice)}</td>
                      <td>{formatToTwoDecimal(item.beginningInventoryAmount)}</td>
                      {/* Product In */}
                      <td>{formatToTwoDecimal(item.productInQuantity)}</td>
                      <td>{formatToTwoDecimal(item.productInAveragePrice)}</td>
                      <td>{formatToTwoDecimal(item.productInAmount)}</td>
                      {/* Product Out */}
                      <td>{formatToTwoDecimal(item.productOutQuantity)}</td>
                      <td>{formatToTwoDecimal(item.productOutAveragePrice)}</td>
                      <td>{formatToTwoDecimal(item.productOutAmount)}</td>
                      {/* Final Inventory */}
                      <td>{formatToTwoDecimal(item.finalInventoryQuantity)}</td>
                      <td>{formatToTwoDecimal(item.finalInventoryAveragePrice)}</td>
                      <td>{formatToTwoDecimal(item.finalInventoryAmount)}</td>
                    </tr>
                  </React.Fragment>,
                )}

                {filteredInventory?.length === 0 && (
                  <tr>
                    <td colSpan="14" className="text-center text-muted py-4">
                      <em>No items found matching your search.</em>
                    </td>
                  </tr>
                )}

                {/* Footer: Total */}
                {/* prettier-ignore */}
                <React.Fragment>
                  <tr className="font-weight-bold table-primary">
                    <td colSpan="2" className="text-right fw-bold">
                      Category Total:
                    </td>
                    {/* Total of Beginning Inventory */}
                    <td className="text-success fw-bold">{formatToTwoDecimal(inventoryReport?.total?.beginningInventoryQuantity)}</td>
                    <td className="text-success fw-bold">{formatToTwoDecimal(inventoryReport?.total?.beginningInventoryAveragePrice)}</td>
                    <td className="text-success fw-bold">{formatToTwoDecimal(inventoryReport?.total?.beginningInventoryAmount)}</td>
                    {/* Total of Product In */}
                    <td className="text-success fw-bold">{formatToTwoDecimal(inventoryReport?.total?.productInQuantity)}</td>
                    <td className="text-success fw-bold">{formatToTwoDecimal(inventoryReport?.total?.productInAveragePrice)}</td>
                    <td className="text-success fw-bold">{formatToTwoDecimal(inventoryReport?.total?.productInAmount)}</td>
                    {/* Total of Product Out */}
                    <td className="text-success fw-bold">{formatToTwoDecimal(inventoryReport?.total?.productOutQuantity)}</td>
                    <td className="text-success fw-bold">{formatToTwoDecimal(inventoryReport?.total?.productOutAveragePrice)}</td>
                    <td className="text-success fw-bold">{formatToTwoDecimal(inventoryReport?.total?.productOutAmount)}</td>
                    {/* Total of Final Inventory */}
                    <td className="text-success fw-bold">{formatToTwoDecimal(inventoryReport?.total?.finalInventoryQuantity)}</td>
                    <td className="text-success fw-bold">{formatToTwoDecimal(inventoryReport?.total?.finalInventoryAveragePrice)}</td>
                    <td className="text-success fw-bold">{formatToTwoDecimal(inventoryReport?.total?.finalInventoryAmount)}</td>
                  </tr>
                </React.Fragment>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductTab;
